/*

/**
 * @description Layout mode enum that defines the available theme modes for the application.
 * These modes control the overall visual appearance and color scheme.
 */
export enum LayoutMode {
    /**
     * @description Light theme mode with bright colors and light backgrounds
     */
    Light = "light",
    
    /**
     * @description Dark theme mode with dark colors and dark backgrounds
     */
    Dark = "dark",
    
    /**
     * @description Auto mode that follows the operating system's theme preference
     * @note Not currently implemented in the LayoutService
     */
    Auto = "auto"
}

/**
 * @description Union type for layout mode that accepts both enum values and string literals.
 * This provides flexibility when working with layout mode values while maintaining type safety.
 * ```
 */
export type LayoutModeType = 'light' | 'dark' | 'auto';

/**
 * @description Union type for layout mode that accepts both enum values and string literals.
 * This is an alias for LayoutModeType and provides backward compatibility.
 */
export type ILayoutMode = LayoutMode | LayoutModeType;

/**
 * @description Layout direction enum that defines the text direction for the application.
 * This is particularly important for internationalization and RTL language support.
 */
export enum LayoutDirection {
    /**
     * @description Right-to-left text direction, used for languages like Arabic and Hebrew
     */
    Rtl = "rtl",
    
    /**
     * @description Left-to-right text direction, used for most Western languages
     */
    Ltr = "ltr"
}

/**
 * @description Union type for layout direction that accepts both enum values and string literals.
 * This provides flexibility when working with layout direction values while maintaining type safety.
 */
export type LayoutDirectionType = 'rtl' | 'ltr';

/**
 * @description Union type for layout direction that accepts both enum values and string literals.
 * This is an alias for LayoutDirectionType and provides backward compatibility.
 */
export type ILayoutDirection = LayoutDirection | LayoutDirectionType;

/**
 * @description Layout language enum that defines the supported languages for the application.
 * These languages correspond to the available localization options.
 */
export enum LayoutLang {
    /**
     * @description Arabic language support with RTL layout
     */
    Ar = "ar",
    
    /**
     * @description English language support with LTR layout
     */
    En = "en"
}

/**
 * @description Union type for layout language that accepts both enum values and string literals.
 * This provides flexibility when working with language values while maintaining type safety.
 */
export type LayoutLangType = 'ar' | 'en'

/**
 * @description Union type for layout language that accepts both enum values and string literals.
 * This is an alias for LayoutLangType and provides backward compatibility.
 */
export type ILayoutLang = LayoutLang | LayoutLangType;